/**
 * useTheme — centralised theme management.
 *
 * To add a new theme:
 *   1. Add a new entry to THEMES below.
 *   2. Add a `html[data-theme="<id>"] { ... }` section in globals.css
 *      with all the `--t-*` variable overrides.
 *   3. Add Tailwind class overrides for that theme if needed.
 *   That's it — everything else picks up automatically.
 */
import { useState, useEffect, useCallback } from 'react';

/* ── Types ── */
export type ThemeId = 'blue' | 'gray' | 'light';

export interface ThemeOption {
  id: ThemeId;
  label: string;
  /** Lucide icon name or SVG key used in the UI: moon / contrast / sun */
  icon: 'moon' | 'contrast' | 'sun';
}

/* ── Theme registry ── */
export const THEMES: ThemeOption[] = [
  { id: 'blue',  label: 'Синяя',   icon: 'moon' },
  { id: 'gray',  label: 'Серая',   icon: 'contrast' },
  { id: 'light', label: 'Светлая', icon: 'sun' },
];

const STORAGE_KEY = 'app-theme';

function readStoredTheme(): ThemeId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && THEMES.some((t) => t.id === v)) return v as ThemeId;
  } catch { /* SSR / incognito */ }
  return 'blue';
}

/* ── Hook ── */
export function useTheme() {
  const [theme, setThemeRaw] = useState<ThemeId>(readStoredTheme);

  /** Apply to DOM + persist */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((id: ThemeId) => setThemeRaw(id), []);

  return { theme, setTheme, themes: THEMES } as const;
}
